import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next'; // Ավելացվեց սա
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHammer, faChevronRight, faChevronDown, 
  faUndo, faCircleNotch, faTools, faFaucet, faBolt, faEllipsisH
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const goldColor = '#c5a059';
const iconMap = { faHammer, faFaucet, faBolt, faEllipsisH };

const MasterCalculator = ({ calculatorId = 5, onResultChange }) => {
  const { t } = useTranslation(); // Թարգմանության հուկը
  const [meta, setMeta] = useState({ navbars: [], groups: [], assets: [] });
  const [metaLoading, setMetaLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [openCategory, setOpenCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [serverResult, setServerResult] = useState(null);
  const [selectedServices, setSelectedServices] = useState({});
  const [address, setAddress] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [navRes, groupRes, assetRes] = await Promise.all([
          axios.get(`${API_BASE}/api/assets/navbars`),
          axios.get(`${API_BASE}/api/assets/asset-groups`),
          axios.get(`${API_BASE}/api/assets/admin/all`)
        ]);

        setMeta({
          navbars: navRes.data.filter(n => n.calculator_id === calculatorId),
          groups: groupRes.data.filter(g => g.calculator_id === calculatorId),
          assets: assetRes.data.data 
        });
        
        const firstGroup = groupRes.data.find(g => g.calculator_id === calculatorId);
        if (firstGroup) setOpenCategory(firstGroup.id);
      } catch (err) {
        console.error("Fetch Error:", err);
      } finally {
        setMetaLoading(false);
      }
    };
    fetchData();
  }, [calculatorId]);

  const updateCount = (itemId, sizeKey, change, type) => {
    setSelectedServices(prev => {
      const currentItem = prev[itemId] || { sizes: {}, count: 0, meters: 0 };
      const newState = { ...prev };

      if (type === 'multi_size') {
        const newSizeCount = Math.max(0, (currentItem.sizes[sizeKey] || 0) + change);
        newState[itemId] = { ...currentItem, sizes: { ...currentItem.sizes, [sizeKey]: newSizeCount } };
      } else if (type === 'meter') {
        const newMeters = Math.max(0, (currentItem.meters || 0) + change);
        newState[itemId] = { ...currentItem, meters: newMeters };
      } else {
        const newCount = Math.max(0, (currentItem.count || 0) + change);
        newState[itemId] = { ...currentItem, count: newCount };
      }

      const itemData = newState[itemId];
      const hasSizes = Object.values(itemData.sizes || {}).some(v => v > 0);
      if (!hasSizes && (itemData.count || 0) === 0 && (itemData.meters || 0) === 0) {
        delete newState[itemId];
      }
      return { ...newState };
    });
  };

  const calculateOnServer = async () => {
    const serviceIds = Object.keys(selectedServices);
    if (serviceIds.length === 0) return alert(t('select_item_alert'));

    setLoading(true);
    try {
        const payload = {
            calculatorId,
            category: 'MASTER',
            selectedServices: selectedServices
        };

        const res = await axios.post(`${API_BASE}/api/calculators/calculate`, payload);

        if (res.data) {
            const { totalPrice, summary, business } = res.data;
            const finalPrice = totalPrice || res.data.total || 0;

            const itemsListStr = (business?.requested_items || [])
                .map(i => `• ${i.name}: ${i.quantity} ${i.unit || t('pcs')}`)
                .join('\n');

            const combinedMessage = `
${t('master_title')}
${t('items_list')}:
${itemsListStr}
-------------------------
${t('total_price')}: ${finalPrice.toLocaleString()} ֏
`.trim();

            setServerResult({ 
                total: finalPrice, 
                summary: summary,
                fullDetailsText: combinedMessage 
            });

            if (onResultChange) {
                onResultChange(finalPrice, summary, {
                    total_price: finalPrice,
                    details: summary,
                    message: combinedMessage,
                    category_name: 'MASTER',
                    requested_items: business?.requested_items || [],
                    subTotal: business?.subTotal || finalPrice,
                    location: address || '',
                    worker_count: 0
                });
            }
            setStep(2);
        }
    } catch (e) {
        console.error("Master Calculation Error:", e);
        alert(t('error'));
    } finally {
        setLoading(false);
    }
  };

  if (metaLoading) return <div style={s.container}><FontAwesomeIcon icon={faCircleNotch} spin /></div>;

  return (
    <div style={s.container}>
      {step === 1 ? (
        <div style={s.flexBody}>
          <div style={s.itemsContainer}>
            {meta.groups.map(group => (
              <div key={group.id} style={s.groupCard}>
                <div style={s.categoryHeader} onClick={() => setOpenCategory(openCategory === group.id ? null : group.id)}>
                  <div style={s.catNameWrapper}>
                    <FontAwesomeIcon icon={iconMap[group.icon] || faTools} style={{color: goldColor}} />
                    <span style={s.catName}>{group.group_name.toUpperCase()}</span>
                  </div>
                  <FontAwesomeIcon icon={openCategory === group.id ? faChevronDown : faChevronRight} size="xs" color={goldColor}/>
                </div>

                {openCategory === group.id && (
                  <div style={s.itemsList}>
                    {meta.assets.filter(a => a.group_id === group.id).map(item => {
                      let options = [];
                      try {
                        options = typeof item.options === 'string' ? JSON.parse(item.options) : (item.options || []);
                      } catch(e) { options = []; }
                      
                      return (
                        <div key={item.id} style={s.itemRow}>
                          <div style={s.itemName}>{item.name}</div>
                          {item.type === 'multi_size' ? (
                            <div style={s.sizeGrid}>
                              {Array.isArray(options) && options.map(size => (
                                <div key={size} style={s.sizeItem}>
                                  <span style={s.sizeLabel}>{size}</span>
                                  <div style={s.counter}>
                                    <button style={s.qtyBtn} onClick={() => updateCount(item.id, size, -1, 'multi_size')}>-</button>
                                    <span style={s.qtyInput}>{selectedServices[item.id]?.sizes?.[size] || 0}</span>
                                    <button style={s.qtyBtn} onClick={() => updateCount(item.id, size, 1, 'multi_size')}>+</button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={s.simpleActionRow}>
                              <span style={s.priceTag}>{item.type === 'meter' ? t('by_meter') : t('by_count')}</span>
                              <div style={s.counter}>
                                <button style={s.qtyBtn} onClick={() => updateCount(item.id, null, -1, item.type)}>-</button>
                                <span style={s.qtyInput}>
                                  {item.type === 'meter' ? `${selectedServices[item.id]?.meters || 0} ${t('meter_unit')}` : (selectedServices[item.id]?.count || 0)}
                                </span>
                                <button style={s.qtyBtn} onClick={() => updateCount(item.id, null, 1, item.type)}>+</button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
          <button style={s.mainBtn} onClick={calculateOnServer} disabled={loading}>
            {loading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : t('calculate_btn')}
          </button>
        </div>
      ) : (
        <div style={s.successCard}>
           <h2 style={s.successTitle}>{t('calculation_result')}</h2>
           <div style={s.receiptCard}>
              <p style={{color: '#64748b', fontSize: '14px'}}>{serverResult?.summary}</p>
              <div style={s.divider} />
              <div style={s.finalRow}>
                <span>{t('total')}</span>
                <span style={{color: goldColor}}>{(serverResult?.total || 0).toLocaleString()} ֏</span>
              </div>
           </div>
           <button style={s.mainBtn} onClick={() => setStep(1)}>{t('back_btn')}</button>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { width: '100%', maxWidth: '600px', margin: '0 auto', padding: '10px' },
  flexBody: { display: 'flex', flexDirection: 'column' },
  itemsContainer: { maxHeight: '450px', overflowY: 'auto' },
  groupCard: { marginBottom: '8px', background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', padding: '15px', cursor: 'pointer', alignItems: 'center' },
  catNameWrapper: { display: 'flex', alignItems: 'center', gap: '10px' },
  catName: { fontWeight: '800', fontSize: '11px', color: '#475569' },
  itemsList: { padding: '0 15px 15px' },
  itemRow: { paddingTop: '12px', paddingBottom: '12px', borderTop: '1px solid #f1f5f9' },
  itemName: { fontSize: '13px', fontWeight: '700', color: '#334155', marginBottom: '10px' },
  sizeGrid: { display: 'flex', flexDirection: 'column', gap: '6px' },
  sizeItem: { background: '#f8fafc', padding: '8px 12px', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sizeLabel: { fontSize: '12px', color: '#64748b' },
  simpleActionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', padding: '8px 12px', borderRadius: '10px' },
  priceTag: { color: goldColor, fontWeight: '700', fontSize: '10px' },
  counter: { display: 'flex', alignItems: 'center', background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' },
  qtyBtn: { border: 'none', background: 'none', color: goldColor, width: '28px', height: '28px', cursor: 'pointer', fontSize: '16px' },
  qtyInput: { minWidth: '35px', textAlign: 'center', fontSize: '12px', fontWeight: '800' },
  mainBtn: { width: '100%', padding: '16px', borderRadius: '14px', border: 'none', background: '#0f172a', color: '#fff', fontWeight: '800', cursor: 'pointer', marginTop: '15px' },
  successCard: { textAlign: 'center', padding: '20px' },
  receiptCard: { background: '#f8fafc', padding: '20px', borderRadius: '15px', border: '1px solid #e2e8f0', textAlign: 'left', marginBottom: '20px' },
  successTitle: { margin: '0 0 20px', fontSize: '18px', fontWeight: '800', color: '#0f172a' },
  divider: { height: '1px', background: '#e2e8f0', margin: '12px 0' },
  finalRow: { display: 'flex', justifyContent: 'space-between', fontWeight: '900', fontSize: '18px' }
};

export default MasterCalculator;